import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { CenterItem, CreateCenterRequest } from '../../types';
import { retreatService } from '../../services/retreatService';

interface CenterState {
  centers: CenterItem[];
  loading: boolean;
  error: string | null;
}

const initialState: CenterState = {
  centers: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchCenters = createAsyncThunk(
  'center/fetchCenters',
  async (_, { rejectWithValue }) => {
    try {
      const response = await retreatService.getCenters();
      return response.data.data; // Extract the actual data array from the paginated response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch centers');
    }
  }
);

export const createCenter = createAsyncThunk(
  'center/createCenter',
  async (centerData: CreateCenterRequest, { rejectWithValue }) => {
    try {
      const response = await retreatService.createCenter(centerData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create center');
    }
  }
);

const centerSlice = createSlice({
  name: 'center',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetCenters: (state) => {
      state.centers = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch centers
      .addCase(fetchCenters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCenters.fulfilled, (state, action) => {
        state.loading = false;
        state.centers = action.payload;
      })
      .addCase(fetchCenters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create center
      .addCase(createCenter.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCenter.fulfilled, (state, action) => {
        state.loading = false;
        state.centers.push(action.payload);
      })
      .addCase(createCenter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetCenters } = centerSlice.actions;
export default centerSlice.reducer;
